import Bowser from 'bowser';
import { v4 } from 'uuid';
import { config } from '../config';

/**
 * Creates a unique correlation ID for tracing requests.
 * Uses crypto.randomUUID() if available, falling back to uuid v4.
 */
export const createCorrelationId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return v4();
};

/**
 * Generates tracing headers for API requests.
 * @param correlationId - The correlation ID for this request
 * @returns Headers object with X-Session-ID and X-Correlation-ID
 */
export const getTracingHeaders = (
  correlationId: string
): { 'X-Session-ID': string; 'X-Correlation-ID': string } => {
  return {
    'X-Session-ID': sessionId,
    'X-Correlation-ID': correlationId
  };
};

type LogLevelValue = 'Debug' | 'Information' | 'Warning' | 'Error' | 'Fatal';

interface PlatformData {
  cookies: boolean;
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  osName: string;
  platform: string;
}

type LogEvent = {
  timestamp: string;
  applicationName: string;
  applicationVersion: string;
  neonUserId: string | undefined;
  sessionId: string | undefined;
  correlationId: string | undefined;
  instanceId: string | undefined;
  level: LogLevelValue;
  platformData?: PlatformData;
};

/**
 * Collects platform data from the browser environment.
 * Returns undefined if user agent parsing fails to avoid breaking logging.
 */
const getPlatformData = (): PlatformData | undefined => {
  try {
    const parser = Bowser.getParser(window.navigator.userAgent);
    const result = parser.getResult();

    return {
      cookies: window.navigator.cookieEnabled,
      browser: result.browser.name || 'Unknown',
      browserVersion: result.browser.version || 'Unknown',
      os: result.os.name || 'Unknown',
      osVersion: result.os.version || 'Unknown',
      osName: result.os.versionName || 'Unknown',
      platform: result.platform.type || 'Unknown'
    };
  } catch {
    return undefined;
  }
};

export const appName = 'neon-support-client';
export const appVersion = config.version;
export const sessionId = v4();

/**
 * Unique identifier for this client instance.
 * Format: <uuid>-<epoch_seconds>
 * Remains stable across log calls but changes on page reload.
 */
export const instanceId = `${v4()}-${Math.floor(Date.now() / 1000)}`;

const platformData = getPlatformData();

let neonUserId: string | undefined;
export const setNeonUserId = (id: string) => {
  neonUserId = id;
};

// Log batch management
let logBatch: unknown[] = [];
let batchTimeout: ReturnType<typeof setTimeout> | null = null;
const BATCH_SIZE = 10;
const BATCH_TIMEOUT_MS = 5000;

const sendLogBatch = () => {
  if (logBatch.length === 0) {
    return;
  }

  const logsToSend = [...logBatch];
  logBatch = [];

  fetch(`${config.basePath}/structuredlog`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(logsToSend)
  }).catch(() => {
    // Silently drop failed batches
  });
};

const queueLog = (logEntry: unknown) => {
  logBatch.push(logEntry);

  if (logBatch.length >= BATCH_SIZE) {
    if (batchTimeout) {
      clearTimeout(batchTimeout);
      batchTimeout = null;
    }
    sendLogBatch();
  } else if (!batchTimeout) {
    batchTimeout = setTimeout(() => {
      batchTimeout = null;
      sendLogBatch();
    }, BATCH_TIMEOUT_MS);
  }
};

const logToConsole = <E extends LogEvent>(logObject: E) => {
  const logLine = JSON.stringify(logObject);
  switch (logObject.level) {
    case 'Debug':
      // eslint-disable-next-line no-console
      console.debug(logLine);
      break;
    case 'Information':
      // eslint-disable-next-line no-console
      console.info(logLine);
      break;
    case 'Warning':
      // eslint-disable-next-line no-console
      console.warn(logLine);
      break;
    case 'Error':
    case 'Fatal':
      // eslint-disable-next-line no-console
      console.error(logLine);
      break;
    default:
      // eslint-disable-next-line no-console
      console.log(logLine);
  }
};

/**
 * Logs a structured event to both the browser console and the server.
 * All logs are enriched with application metadata and batched for efficient transmission.
 *
 * @param event - The event object to log
 * @param event.event - Event identifier in snake_case format
 * @param event.level - Optional log level (defaults to 'Information')
 */
export const logEvent = <E extends { event: string; level?: LogLevelValue }>(event: E) => {
  const logObject: LogEvent = {
    ...event,
    timestamp: new Date().toISOString(),
    applicationName: appName,
    applicationVersion: appVersion,
    neonUserId,
    sessionId,
    correlationId: sessionId,
    instanceId,
    platformData,
    level: event.level || 'Information'
  };

  logToConsole(logObject);
  queueLog(logObject);
};

/**
 * Initializes global error handlers to capture and log all JavaScript errors.
 * Should be called once during application bootstrap.
 */
export const initializeGlobalErrorHandlers = () => {
  window.addEventListener('error', (event) => {
    logEvent({
      event: 'js_error',
      level: 'Error',
      message: event.message,
      source: event.filename || undefined,
      lineno: event.lineno || undefined,
      colno: event.colno || undefined,
      errorName: event.error?.name,
      errorMessage: event.error?.message,
      errorStack: event.error?.stack
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    logEvent({
      event: 'unhandled_promise_rejection',
      level: 'Error',
      reasonType: typeof reason,
      reasonMessage: reason?.message || String(reason),
      reasonStack: reason?.stack
    });
  });
};

// Exported for testing
export { logBatch as _logBatch, sendLogBatch as _sendLogBatch, BATCH_SIZE, BATCH_TIMEOUT_MS };
